import { useCallback, useEffect, useRef, useState } from 'react';

import {
  EllipsisVerticalIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SquarePenIcon,
  Trash2Icon,
  UploadIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Avatar } from '@/components/atoms/Avatar/Avatar/Avatar';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { VersionChanges } from '@/components/organisms/Modals/ModalPublishVersion/VersionChanges';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAllRules } from '@/hooks/useAllRules';
import useVersions, { API as versionsApi } from '@/hooks/useVersions';
import { cn } from '@/lib/utils';
import { selectAgentNameById } from '@/redux/presence/selectors';
import { selectBrainId } from '@/redux/session/selectors';
import { RootState } from '@/shared/types/state';
import { Version } from '@/shared/types/version';
import { timeFromNow } from '@/util/dateUtils';
import { isKeyEnter } from '@/util/util';

import { RuleLink } from './RuleLink';
import { useVersionsTable } from './useVersionsTable';

import styles from './Versions.module.scss';
type Props = { version: Version; index: number; versions: Version[] };

export function hasChanges(components) {
  return components.some((component) => {
    if (component.component !== 'users') {
      return (
        component.added.length > 0 || component.removed.length > 0 || component.modified.length > 0
      );
    }
    return false;
  });
}

export const TimeLineBox = ({ version, index, versions }: Props) => {
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const createdByName = useSelector((state: RootState) =>
    selectAgentNameById(state, version.created_by)
  );
  const { handleRestoreVersion, handleDeleteVersion } = useVersionsTable();
  const { exportVersion } = useVersions({ brainId });
  const [seeAll, setSeeAll] = useState(false);
  const [diffs, setDiffs] = useState([]);
  const { rulesWithDeskName } = useAllRules({ brainId, brainVersion: version?.version });
  const calledRef = useRef(false);
  useEffect(() => {
    const from = versions[index + 1]?.version;
    const to = versions[index]?.version;

    if (brainId && from && diffs.length === 0 && !calledRef.current) {
      calledRef.current = true;
      versionsApi
        .getDiff(brainId, from, to)
        .then((data) => {
          setDiffs(data);
        })
        .catch((error) => {
          console.error('Error getting versions diff', error);
        });
    }

    return () => {
      calledRef.current = false;
    };
  }, [brainId, diffs.length, index, versions]);

  const ruleCount = rulesWithDeskName?.length;
  const firstRule = rulesWithDeskName[0];
  const restRules = rulesWithDeskName.slice(1) || [];

  const handleSeeAll = useCallback(() => {
    setSeeAll((seeAll) => !seeAll);
  }, []);

  const handleSeeAllKeyDown = useCallback(
    (event) => {
      if (isKeyEnter(event)) {
        handleSeeAll();
      }
    },
    [handleSeeAll]
  );

  return (
    <>
      <span className={styles.timeLineHeader}>
        <span className={styles.title}>
          <span className="text-sm leading-5 font-semibold">
            {t('common.version')} {version?.version}
          </span>
          {version?.last_used_at && (
            <span className="text-sm leading-5 font-normal text-foreground-muted">
              {t('common.last_used')}:{' '}
              <TimestampTooltip timestamp={version.last_used_at}>
                {timeFromNow(version.last_used_at)}
              </TimestampTooltip>
            </span>
          )}
          {ruleCount > 0 && (
            <InfoTooltip
              content={
                restRules?.length > 0 ? (
                  <>
                    {restRules.map((rule) => (
                      <span key={rule.rule_id} className={styles.linkWrapper}>
                        <RuleLink rule={rule} />
                      </span>
                    ))}
                  </>
                ) : null
              }
              size="auto"
              variant="rich"
              className="flex max-w-[1000px] flex-col justify-center border border-stroke bg-background text-sm leading-5 font-normal text-informational"
            >
              <div className={styles.linkBox}>
                <RuleLink rule={firstRule} />
                {ruleCount > 1 && (
                  <span className="text-sm leading-5 font-normal text-foreground-muted">
                    +{ruleCount - 1}
                  </span>
                )}
              </div>
            </InfoTooltip>
          )}
        </span>
        <span className={styles.menu}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost-neutral" size="icon-sm" aria-label="version options">
                <EllipsisVerticalIcon size={20} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleRestoreVersion(version?.version)}>
                <SquarePenIcon size={16} className="text-foreground-muted" />
                {t('common.restore')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportVersion(version?.version)}>
                <UploadIcon size={16} className="text-foreground-muted" />
                {t('common.export')}
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onClick={() => handleDeleteVersion(version?.version)}
              >
                <Trash2Icon size={16} />
                {t('common.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </span>
      </span>
      {version.description && (
        <span
          className={cn(
            'text-sm leading-5 font-normal text-foreground-muted',
            styles.timeLineDescription
          )}
        >
          {version.description}
        </span>
      )}
      <span className={styles.timeLineSubHeader}>
        <span className={styles.createdBy}>
          <span className="text-sm leading-5 font-normal text-foreground-muted">
            {t('common.created_by')}
          </span>
          <InfoTooltip content={createdByName} size="auto">
            <span>
              <Avatar userId={version.created_by} size="small" hideStatus />
            </span>
          </InfoTooltip>
        </span>
        <span className="text-sm leading-5 font-normal text-foreground-muted">
          <TimestampTooltip timestamp={version.created}>
            {timeFromNow(version.created)}
          </TimestampTooltip>
        </span>
      </span>
      <span className={styles.timeLineMetaData}>
        {hasChanges(diffs) && (
          <span
            className={styles.seeAll}
            role="button"
            onClick={handleSeeAll}
            onKeyDown={handleSeeAllKeyDown}
            tabIndex={0}
          >
            <span className="text-sm leading-5 font-normal text-informational">
              {t('common.see_all')}{' '}
            </span>
            {seeAll ? (
              <ChevronUpIcon size={16} className="text-foreground-muted" />
            ) : (
              <ChevronDownIcon size={16} className="text-foreground-muted" />
            )}
          </span>
        )}
        {seeAll && hasChanges(diffs) && (
          <div className={styles.changes}>
            <VersionChanges versionsDiff={diffs} loadingChanges={false} padding={false} />
          </div>
        )}
      </span>
    </>
  );
};
